import { useState, useRef, useEffect } from "react";
import { Button } from "reactstrap";
import ThemeModal from "../ThemeModal";

const PdfViewerModal = ({
  isOpen,
  onClose,
  pdfUrl,
  fileName = "Document",
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen && pdfUrl) {
      setLoading(true);
      setError(null);
    }
  }, [isOpen, pdfUrl]);

  // ESC close (professional UX)
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError("Failed to load PDF preview.");
  };

  const downloadPdf = () => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = fileName.endsWith(".pdf")
      ? fileName
      : `${fileName}.pdf`;
    link.click();
  };

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={onClose}
      title={fileName}
      size="lg"
      modalBodyClass="p-0"
    >
      <div className="pdf-container">

        {/* Loading State */}
        {loading && !error && (
          <div className="viewer-state">
            <div className="pdf-loader" />
            <span className="state-text">
              Preparing secure preview…
            </span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="viewer-state">
            <div className="error-icon">
              <i className="bx bxs-file-pdf"></i>
            </div>
            <h6 className="fw-semibold">Preview unavailable</h6>
            <p className="text-muted small mb-3">{error}</p>
            <Button
              color="primary"
              size="sm"
              onClick={downloadPdf}
              className="px-4"
            >
              <i className="bx bx-download me-2"></i>
              Download PDF
            </Button>
          </div>
        )}

        {/* PDF Iframe */}
        <iframe
          ref={iframeRef}
          src={pdfUrl}
          title="PDF Viewer"
          className={`pdf-frame ${
            loading || error ? "hidden" : "visible"
          }`}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      </div>

      {/* Professional Styling */}
      <style>{`
        .pdf-container {
          position: relative;
          height: 75vh;
          background: linear-gradient(135deg, #f8fafc, #f1f3f5);
          border-radius: 14px;
          overflow: hidden;
          display: flex;
        }

        .pdf-frame {
          width: 100%;
          height: 100%;
          border: none;
          background: white;
          transition: opacity 0.3s ease;
          border-radius: 12px;
          box-shadow: 0 15px 40px rgba(0,0,0,0.08);
        }

        .pdf-frame.hidden {
          opacity: 0;
          pointer-events: none;
        }

        .pdf-frame.visible {
          opacity: 1;
        }

        .viewer-state {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 14px;
          background: linear-gradient(135deg, #f8fafc, #f1f3f5);
        }

        .state-text {
          font-weight: 500;
          color: #495057;
        }

        .pdf-loader {
          width: 42px;
          height: 42px;
          border: 3px solid #e9ecef;
          border-top: 3px solid #228be6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .error-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #fff5f5;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: #e03131;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .pdf-container {
            height: 70vh;
          }
        }
      `}</style>
    </ThemeModal>
  );
};

export default PdfViewerModal;